import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  Input,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation
} from '@angular/core';
import { faArrowRight, faCircleNotch, faLocationDot, faMap, faTimes } from '@fortawesome/free-solid-svg-icons';
import { take } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { MovieService } from '@usheru-hff/sdk/movie';
import { getMovieGenres, getMovieTags, PerformanceGroup } from '@usheru-hff/shared/domain';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { PanelPerformanceAnimation } from './performance.animations';
import { ShowtimesStoreService } from '../../shared/showtimes.service';

export interface DateItem {
  position: number;
  date?: Date;
  active: boolean;
  hours: HourItem[];
  dayLiteral: string;
  dateLiteral: string;
}

export interface HourItem {
  position: number;
  hourDate?: Date;
  hour: string;
  disabled?: boolean;
  performance: number;
  attributes?: string[];
  redirectionLink?: string;
  showtimeDescription?: string;
  showtimeStatusMessage?: { key: string; label: string };
  tbcActive?: boolean;
  fandangoUrl?: string;
}

/**
 * Component that shows book information for a cinema.
 *
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - update translate text with an observable to the current language
 * //TODO barbeitoalex propostal - move component folder to the root of components
 */
@Component({
  selector: 'ush-performance',
  templateUrl: './performance.component.html',
  styleUrls: ['./performance.component.scss'],
  animations: PanelPerformanceAnimation,
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PerformanceComponent implements OnInit, AfterViewInit {
  readonly TBC_TRANSLATE_KEY = 'showtimes.hour.tbc';
  readonly TBC_LONG_TRANSLATE_KEY = 'showtimes.hour.tbc-long';

  @HostBinding('id') id!: string;

  /**
   * @docsNotRequired
   */
  _perfGroup: PerformanceGroup;

  get perfGroup(): PerformanceGroup {
    return this._perfGroup;
  }

  @Input() set perfGroup(perfGroup: PerformanceGroup) {
    this._perfGroup = perfGroup;
    this.id = `cinema-${perfGroup.cinema.id}`;
  }

  @Input()
  date: Date;

  @Input({ transform: booleanAttribute }) newWorkflow = false;

  /**
   *  Basic usage of `ush-performance` won´t include this property.
   *  When used in conjuction with `ush-performance-header` one might one to use `hideAddress`,
   * since there is an address showing already on the **header** associated component.
   * */
  @Input({ transform: booleanAttribute }) hideAddress = false;

  /**
   * If it is active show the {@link ModalMapComponent} with the cinema location.
   */
  showMap = false;
  /**
   * @deprecard Don't found any use
   */
  shown = false;
  /**
   * @docsNotRequired
   */
  dates: DateItem[];
  /**
   * @docsNotRequired
   */
  hours: HourItem[];
  /**
   * @docsNotRequired
   */
  dateSelected: DateItem;
  is12HourFormat = false;
  activeDateAttributes = false;
  /**
   * Status to know if the user is waiting to get the sale status.
   */
  loading = true;
  /**
   * Performance id selected. Used to go to the buy page.
   */
  performanceSelected: number;
  /**
   * Status to know if the reddirection still in the app.
   */
  internalLink = false;
  /**
   * Status to know if the action button is disabled. Sets from the sale status.
   */
  arrowIcon = faArrowRight;
  spinnerIcon = faCircleNotch;
  locationIcon = faLocationDot;
  closeIncon = faTimes;
  mapIcon = faMap;
  movie = this.showtimesStore.movie;
  inlineMap = this.showtimesStore.inlineMap;

  readonly fandangoImg = 'https://cdnstatic.usheru.com/img/others/fandango-logo.png';

  constructor(
    private translationService: TranslationsService,
    private trackingService: TrackingService,
    private movieService: MovieService,
    private showtimesStore: ShowtimesStoreService,
    private cd: ChangeDetectorRef,
    private location: Location,

    @Inject(DOCUMENT) private document: Document,
    @Inject(ENVIRONMENT) private env: Environment,
    @Inject(PLATFORM_ID) private platformId: ObjectConstructor
  ) {
    this.is12HourFormat = this.env.is12HourFormat;
    this.activeDateAttributes = this.env?.showtimeStreamingConfig?.performanceHourAttributes;
    this.newWorkflow = this.env.showtimeStreamingConfig?.isNewFlow;
  }

  /**
   *
   */
  ngOnInit() {
    if (this.newWorkflow) {
      this.getHoursOnDate(this.date);
    } else {
      this.datesByDay();
    }
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
  }

  getShowtimePreparedLink(x: string, hour: HourItem): string | undefined {
    if (!x) return;

    const utmParams = [
      ['utm_source', this.document.location.hostname],
      ['utm_medium', 'usheru'],
      ['utm_campaign', this.movie().friendlyUrl]
    ];

    let url;
    try {
      url = new URL(x);
    } catch (e) {
      console.error(`Invalid URL at performance ${hour.performance}, ${hour.hourDate.toDateString()}: ${x}`, e);
      return '';
    }

    utmParams.forEach(param => {
      url.searchParams.append(param[0], param[1]);
    });

    // HARDCORED FIX rio-cinema-london
    if (x.indexOf('https://riocinema.org.uk') == 0 || (x.indexOf('glasgowfilm.org') != -1 && x.indexOf('#') > -1)) {
      const newUrl = `${x}?utm_source=${this.document.location.hostname}&utm_medium=usheru&utm_campaign=${this.movie().friendlyUrl}`;
      return newUrl;
    }
    // HARDCORED FIX for screenaustralia
    if (x.indexOf('https://www.eventcinemas.com.au') == 0) {
      const newUrl = x;
      return newUrl;
    }

    return url.toString();
  }

  /**
   * Add tracking after press the action button.
   */
  goToBuy() {
    this.addTracking();
  }

  /**
   * Add tracking after press the action button.
   *
   * //TODO barbeitoalex propostal - the process with the showtimeLink don't have sense. The a has a href and here does to a reddirection.
   * //TODO barbeito propostal - is the other case is fixed could be merged with {@link goToBuy}.
   */
  goToExternalBuy(hour: HourItem) {
    const showtimeLink = hour?.redirectionLink || '';
    this.addTracking(false);
    // making sure external link starts with http or https
    if (showtimeLink) {
      if (showtimeLink.indexOf('http://') == 0 || showtimeLink.indexOf('https://') == 0) {
        window.open(showtimeLink, '_blank');
      } else {
        window.open('http://' + showtimeLink, '_blank');
      }
    }
  }

  /**
   * @docsNotRequired
   * @param internal
   */
  private addTracking(internal = true) {
    this.trackingService.trackEvent({
      action: internal ? 'AddToCart' : 'CinemaRedirect',
      properties: {
        label: `${this.movie().title} @ ${this.perfGroup.cinema.name}`,
        category: 'CinemaPurchase',
        movie: this.movie().title,
        idMovie: this.movie().id,
        imdb: this.movie().imdb,
        cinema: this.perfGroup.cinema.name,
        idCinema: this.perfGroup.cinema.id,
        cinema_city: this.perfGroup.cinema.city,
        tagsMovie: getMovieTags(this.movie()),
        genresMovie: getMovieGenres(this.movie())
      }
    });
  }

  onFandangoRedirect() {
    this.trackingService.trackEvent({
      action: 'FandangoRedirect',
      properties: {
        label: `${this.movie().title} @ ${this.perfGroup.cinema.name}`,
        category: 'CinemaPurchase',
        movie: this.movie().title,
        idMovie: this.movie().id,
        imdb: this.movie()?.imdb,
        cinema: this.perfGroup.cinema.name,
        idCinema: this.perfGroup.cinema.id,
        cinema_city: this.perfGroup.cinema.city,
        tagsMovie: getMovieTags(this.movie()),
        genresMovie: getMovieGenres(this.movie())
      }
    });
  }
  /**
   * @docsNotRequired
   */
  openMap() {
    this.showMap = true;
    this.trackingService.trackEvent({
      action: 'OpenMap',
      properties: {
        category: 'MovieShowtimes',
        label: `${this.movie().title} @ ${this.perfGroup.cinema.name}`,
        movie: this.movie().title,
        idMovie: this.movie().id,
        imdb: this.movie()?.imdb,
        cinema: this.perfGroup.cinema.name,
        idCinema: this.perfGroup.cinema.id,
        cinema_city: this.perfGroup.cinema.city,
        tagsMovie: getMovieTags(this.movie()),
        genresMovie: getMovieGenres(this.movie())
      }
    });
    this.cd.detectChanges();
  }

  closeInlineMap() {
    this.showMap = false;
    this.cd.detectChanges();
  }

  // CHECK: New Modal Changes
  /**
   * @docsNotRequired
   * //TODO barbeitoalex propostal - remane to "hideMap"?Pho
   */
  onHideMap() {
    this.showMap = false;
    this.cd.detectChanges();
  }

  /**
   * Sets {@link dates} and the hours for that dates.
   *
   * //TODO barbeitoalex propostal - the name coluld be more descriptive.
   */
  private datesByDay() {
    this.dates = [];
    this.hours = [];

    const attributes = this.activeDateAttributes ? this.perfGroup?.parsedAttributeSlugs : undefined;
    const perfIds = this.perfGroup.parsedIds;
    const tmpDates = this.perfGroup.parsedLocalTimes;
    const datesObj: string[] = [];
    const active = false;
    tmpDates.forEach((date, i) => {
      const tmp = `${date.getDate()}-${date.getMonth()}`;
      if (!datesObj.includes(tmp)) {
        datesObj.push(tmp);
        this.dates.push({
          date,
          active,
          position: i,
          hours: [
            {
              position: 0,
              hourDate: date,
              hour: this.getHourFormat(date),
              performance: perfIds[i],
              attributes: attributes ? attributes[i] : undefined
            }
          ],
          dayLiteral: this.getSimplifiedDay(date),
          dateLiteral: this.getSimplifiedDate(date)
        });
      } else {
        this.dates.forEach(o => {
          if (o.date.getDate() === date.getDate() && o.date.getMonth() === date.getMonth()) {
            o.hours.push({
              position: o.hours.length,
              hourDate: date,
              hour: this.getHourFormat(date),
              performance: perfIds[i],
              attributes: attributes ? attributes[i] : undefined
            });
          }
        });
      }
    });

    if (this.newWorkflow) {
      this.dateSelected = this.dates.find(
        d => d.date.getDate() === this.date.getDate() && d.date.getMonth() === this.date.getMonth()
      );
    } else {
      this.dateSelected = this.dates[0];
    }

    this.dates[0].active = true;
    this.checkTBCdates();
    this.hours = this.dateSelected.hours;
    if (!this.shown) {
      this.dates[0].hours.forEach(hour => {
        this.checkStatus(hour, { tbc: hour.tbcActive });
      });
      this.shown = true;
    } else {
      this.shown = false;
    }
  }

  getHoursOnDate(date: Date) {
    this.hours = [];
    const attributes = this.activeDateAttributes ? this.perfGroup?.parsedAttributeSlugs : undefined;
    const perfIds = this.perfGroup.parsedIds;
    const tmpDates = this.perfGroup.parsedLocalTimes;
    tmpDates.forEach((date, i) => {
      const tmp = `${date.getDate()}-${date.getMonth()}`;
      const dateStr = `${this.date.getDate()}-${this.date.getMonth()}`;
      if (tmp == dateStr)
        this.hours.push({
          position: 0,
          hourDate: date,
          hour: this.getHourFormat(date),
          performance: perfIds[i],
          attributes: attributes ? attributes[i] : undefined
        });
    });
    this.hours.forEach(hour => {
      this.checkStatus(hour, { tbc: hour.tbcActive });
    });
  }

  checkTBCdates() {
    // Reference date to know if the status will be TBC
    const tbcReference = new Date(this.dates[0].date);
    // 12:01AM
    tbcReference.setHours(0, 1);
    //
    this.dates.forEach(d => {
      const hoursDate = d.hours;
      hoursDate.map(h => {
        // if hour is the same as the TBC reference change the hour string
        if (h.hourDate?.getTime() === tbcReference.getTime()) {
          h.hour = this.translationService.translate(this.TBC_TRANSLATE_KEY);
          h.tbcActive = true;
        }
        return h;
      });
    });
  }

  /**
   * Event to update the available hours after select a day.
   */
  setHours(date: DateItem) {
    this.dates.forEach(d => {
      if (d.position === date.position) {
        d.active = true;
        this.dateSelected = d;
        this.hours = d.hours;
      } else {
        d.active = false;
      }
    });

    date.hours.forEach(h => {
      this.checkStatus(h, { tbc: h.tbcActive });
    });

    this.trackingService.trackEvent({
      action: 'SelectDate',
      properties: {
        category: 'MovieShowtimes',
        label: `${this.movie().title} @ ${this.perfGroup.cinema.name} @ ${date.date.toLocaleDateString()}`,
        movie: this.movie().title,
        idMovie: this.movie().id,
        imdb: this.movie()?.imdb,
        cinema: this.perfGroup.cinema.name,
        idCinema: this.perfGroup.cinema.id,
        cinema_city: this.perfGroup.cinema.city,
        tagsMovie: getMovieTags(this.movie()),
        genresMovie: getMovieGenres(this.movie())
      }
    });

    this.cd.detectChanges();
  }

  /**
   * After select an hour runs {@link checkStatus}. Also adds tracking
   * to this event.
   * @param hour
   */
  changeHour(hour: HourItem) {
    this.hours.forEach(h => {
      if (h.position === hour.position) {
        this.trackingService.trackEvent({
          action: 'SelectTime',
          properties: {
            category: 'MovieShowtimes',
            label: `${this.movie().title} @ ${this.perfGroup.cinema.name} @ ${hour.hour}`,
            movie: this.movie().title,
            idMovie: this.movie().id,
            imdb: this.movie()?.imdb,
            cinema: this.perfGroup.cinema.name,
            idCinema: this.perfGroup.cinema.id,
            cinema_city: this.perfGroup.cinema.city,
            tagsMovie: getMovieTags(this.movie()),
            genresMovie: getMovieGenres(this.movie())
          }
        });
      }
    });
    // logic that book now button was doing
    if (!hour.disabled) {
      this.goToExternalBuy(hour);
    }
    this.cd.detectChanges();
  }

  /**
   * From a {@link Performance} getted from {@link PerformanceGroup} date, hour.
   * Form this set the action button actions.
   *
   * //TODO barbeitoalex propostal - change subscribe to lastValueFrom
   * @param id
   */
  checkStatus(hour: HourItem, args?: { tbc?: boolean }) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const { tbc } = args || { tbc: false };
    //
    if (tbc) {
      this.loading = false;
      hour.showtimeStatusMessage = {
        key: this.TBC_LONG_TRANSLATE_KEY,
        label: this.translationService.translate(this.TBC_LONG_TRANSLATE_KEY)
      };
      hour.disabled = true;
      return;
    }
    this.internalLink = false;
    hour.fandangoUrl = null;
    this.movieService
      .getPerformance(hour.performance)
      .pipe(take(1))
      .subscribe(
        performance => {
          this.loading = false;
          if (performance.description) {
            hour.showtimeDescription = performance.description;
          }
          hour.fandangoUrl = performance?.fandangoUrl;

          switch (performance.saleStatus) {
            case 10:
              this.internalLink = true;
              this.performanceSelected = performance.id;
              hour.redirectionLink = performance.usheruUrl;
              hour.disabled = false;
              break;
            case 20:
            case 21:
            case 25:
            case 99:
              hour.disabled = false;
              hour.redirectionLink = performance.cinemaUrl || performance.cinema.website || null;
              if (!hour.redirectionLink) {
                hour.showtimeStatusMessage = {
                  key: 'public.showtime.status.soon',
                  label: $localize`:@@public.showtime.status.soon:Coming soon`
                };
                hour.disabled = true;
              }

              break;
            case 50:
              hour.showtimeStatusMessage = {
                key: 'public.showtime.status.sold',
                label: $localize`:@@public.showtime.status.sold:All Tickets Sold`
              };
              break;
            case 52:
              hour.showtimeStatusMessage = {
                key: 'public.showtime.status.close',
                label: $localize`:@@public.showtime.status.close:Online sales closed`
              };
              break;
            case 55:
              hour.showtimeStatusMessage = {
                key: 'public.showtime.status.cancel',
                label: $localize`:@@public.showtime.status.cancel:Cancelled`
              };
              break;
            case 70:
              hour.showtimeStatusMessage = {
                key: 'public.showtime.status.soon',
                label: $localize`:@@public.showtime.status.soon:Coming soon`
              };
              break;
          }
          hour.redirectionLink = this.getShowtimePreparedLink(hour.redirectionLink, hour);
          this.cd.detectChanges();
        },
        err => {
          this.loading = false;
          console.error(err);
        }
      );
  }

  /**
   * @docsNotRequired
   *
   * //TODO barbeitoalex propostal - rename to get "get2digitHour"
   * @param date
   * @returns
   */
  getHourFormat(date: Date): string {
    return date.toLocaleTimeString(this.translationService.currentLanguage().code, {
      hour: 'numeric',
      hour12: this.is12HourFormat,
      minute: '2-digit'
    });
  }

  /**
   * @deprecard Don't found any use
   * @param num
   * @returns
   */
  formatNumber(num: number): string {
    return num <= 9 ? `0${num}` : `${num}`;
  }

  /**
   * @docsNotRequired
   *
   * //TODO add return type
   * @param date
   * @returns
   */
  getSimplifiedDate(date: Date) {
    return date.toLocaleDateString(this.translationService.currentLanguage().code, {
      day: 'numeric',
      month: 'short'
    });
  }

  /**
   * @docsNotRequired
   *
   * //TODO add return type
   * @param date
   * @returns
   */
  getSimplifiedDay(date: Date) {
    return date.toLocaleDateString(this.translationService.currentLanguage().code, {
      weekday: 'short'
    });
  }

  // CHECK: New Modal Changes
  /**
   * Checks if the {@link perfGroup} has a valid address.
   * @returns
   */
  addressExists(): boolean {
    return !this.perfGroup?.cinema?.place?.address?.includes('null null');
  }

  openMapKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.openMap();
    }
  }
}
